/**
 * Новости приложения: `GET/POST /admin/news`, `GET/PUT/DELETE /admin/news/{id}`,
 * фото — `POST /admin/news/media`.
 *
 * На бэкенде этих ручек ещё нет — по просьбе заказчика раздел работает на
 * mock-хранилище в браузере (`@dorixona/api/news`), за флагом
 * `VITE_FEATURE_NEWS`. Источник выбирает `VITE_NEWS_MOCK`: когда ручки
 * появятся, переключается он, а экраны остаются теми же.
 */
import { useMemo } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mediaUrl } from '@dorixona/api'
import {
  createHttpNewsBackend,
  createMockNewsStore,
  type NewsBackend,
  type NewsDto,
  type NewsInput,
} from '@dorixona/api/news'
import { nowMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'
import { shrinkImage } from '../stories/image'

export interface News {
  id: number
  titleRu: string
  titleUz: string
  leadRu: string
  leadUz: string
  /** HTML редактора. */
  bodyRu: string
  bodyUz: string
  photoKey: string
  /** Готовый адрес фото; пусто — фото нет. */
  photoUrl: string
  published: boolean
  /** ISO 8601; у черновика может не быть. */
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

export type NewsToSave = Omit<News, 'createdAt' | 'updatedAt'>

export function toNews(dto: NewsDto): News {
  return {
    id: dto.id,
    titleRu: dto.title_ru,
    titleUz: dto.title_uz,
    leadRu: dto.lead_ru,
    leadUz: dto.lead_uz,
    bodyRu: dto.body_ru,
    bodyUz: dto.body_uz,
    photoKey: dto.photo_key,
    photoUrl: mediaUrl(dto.photo_url, env.apiBaseUrl),
    published: dto.is_published,
    publishedAt: dto.published_at,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  }
}

export function toNewsInput(news: NewsToSave): NewsInput {
  return {
    title_ru: news.titleRu,
    title_uz: news.titleUz,
    lead_ru: news.leadRu,
    lead_uz: news.leadUz,
    body_ru: news.bodyRu,
    body_uz: news.bodyUz,
    photo_key: news.photoKey,
    is_published: news.published,
    published_at: news.publishedAt,
  }
}

/** Откуда берутся новости: mock в браузере или ручки API. */
export function useNewsBackend(): NewsBackend {
  const api = useApi()
  return useMemo(
    () =>
      env.newsMock
        ? createMockNewsStore({ storage: window.localStorage, now: nowMs })
        : createHttpNewsBackend(api),
    [api],
  )
}

export interface NewsListQuery {
  page: number
  pageSize: number
  q: string
}

export function useNewsList(query: NewsListQuery) {
  const backend = useNewsBackend()
  return useQuery({
    queryKey: keys.news({ ...query }),
    queryFn: async () => {
      const page = await backend.list(query)
      return { items: page.items.map(toNews), total: page.total }
    },
    placeholderData: keepPreviousData,
  })
}

export function useNews(id: number, options: { enabled?: boolean } = {}) {
  const backend = useNewsBackend()
  return useQuery({
    queryKey: keys.news({ id }),
    queryFn: async () => toNews(await backend.get(id)),
    enabled: options.enabled ?? true,
  })
}

export function useSaveNews() {
  const backend = useNewsBackend()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (news: NewsToSave) =>
      toNews(
        news.id
          ? await backend.update(news.id, toNewsInput(news))
          : await backend.create(toNewsInput(news)),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.newsAll }),
  })
}

export function useDeleteNews() {
  const backend = useNewsBackend()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => backend.remove(id),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.newsAll }),
  })
}

/** Фото новости. В mock ужимается: хранилище браузера маленькое. */
export function useUploadNewsPhoto() {
  const backend = useNewsBackend()
  return useMutation({
    mutationFn: async (file: File) => {
      const media = await backend.upload(env.newsMock ? await shrinkImage(file) : file)
      return { key: media.key, url: mediaUrl(media.url, env.apiBaseUrl) }
    },
  })
}
